import type { PropsWithChildren } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import { createIntl, createIntlCache, RawIntlProvider } from 'react-intl'
import {
  DEFAULT_LOCALE,
  getLocaleDirection,
  getHtmlLang,
  getLocaleWithWesternDigits,
} from './util/locale'
import { setCurrentIntl } from './util/currentIntl'
export type LocaleMessages = Record<string, string>
const emptyMessages: LocaleMessages = {}

type MessageEntry = {
  defaultMessage: string
  description?: string
}

interface I18nProviderProps {
  messages?: LocaleMessages
  messagesLocale?: string
}

function mapLocaleMessages(
  messages: Record<string, MessageEntry>,
): LocaleMessages {
  return Object.fromEntries(
    Object.entries(messages).map(([key, value]) => [
      key,
      typeof value === 'string' ? value : value.defaultMessage,
    ]),
  )
}

export function shouldSkipRuntimeLocaleMessages(
  locale: string,
  buildLocale: string | undefined = process.env.BUILD_LOCALE,
) {
  return locale === DEFAULT_LOCALE || buildLocale === locale
}

export function loadLocaleMessages(locale: string): Promise<LocaleMessages> {
  if (shouldSkipRuntimeLocaleMessages(locale)) {
    return Promise.resolve(emptyMessages)
  }
  return import(
    // Playwright CT: skip Vite's `./lang/*.json` glob so translation diffs do not
    // land on every spec's import graph. Rsbuild/rspack ignore this comment. A failed
    // load still resolves to emptyMessages below — do not drop the ignore as a cleanup.
    /* @vite-ignore */
    `./lang/${locale}.json`
  )
    .then((mod) =>
      mapLocaleMessages(mod.default as Record<string, MessageEntry>),
    )
    .catch(() => emptyMessages)
}

export function I18nProvider({
  messages,
  messagesLocale,
  children,
}: PropsWithChildren<I18nProviderProps>) {
  const geoLocale = useAppSelector((state) => state.geo.locale)
  const locale = typeof geoLocale === 'string' ? geoLocale : DEFAULT_LOCALE
  const [localeMessages, setLocaleMessages] = useState<LocaleMessages>(
    messages ?? emptyMessages,
  )

  useEffect(() => {
    if (messages && locale === messagesLocale) {
      setLocaleMessages(messages)
      return
    }

    let isMounted = true

    loadLocaleMessages(locale).then((loadedMessages) => {
      if (isMounted) {
        setLocaleMessages(loadedMessages)
      }
    })

    return () => {
      isMounted = false
    }
  }, [locale, messages, messagesLocale])

  useEffect(() => {
    document.documentElement.lang = getHtmlLang(locale)
    document.documentElement.dir = getLocaleDirection(locale)
  }, [locale])

  const intl = useMemo(() => {
    return createIntl(
      {
        locale: getLocaleWithWesternDigits(locale),
        messages: localeMessages,
        defaultLocale: DEFAULT_LOCALE,
        onError(err) {
          if (err.code === 'MISSING_TRANSLATION') {
            return
          }
          console.error(err)
        },
      },
      createIntlCache(),
    )
  }, [locale, localeMessages])

  setCurrentIntl(intl)

  return <RawIntlProvider value={intl}>{children}</RawIntlProvider>
}
